import React, { useState, useEffect, useCallback } from 'react';
import { Search, Filter, Edit2, Save, X, Plus, Trash2, DollarSign, RefreshCw } from 'lucide-react';
import { sitePricingAPI } from '../../services/sitePricingAPI';

interface SiteWithPricing {
  _id: string;
  siteId: {
    _id: string;
    name: string;
    code: string;
    email?: string;
    phone?: string;
  };
  basePrice: {
    amount: number;
    currency: string;
    billingPeriod: string;
  };
  billingInfo: {
    companyName: string;
    billingAddress: {
      street: string;
      city: string;
      postcode: string;
      country: string;
    };
    contactEmail: string;
    contactPhone: string;
    vatNumber: string;
  };
  premiumFeaturePricing: {
    financialReports: {
      enabled: boolean;
      price: number;
    };
    customerPortal: {
      enabled: boolean;
      price: number;
    };
    delivery: {
      enabled: boolean;
      price: number;
    };
  };
  discounts: {
    percentage: number;
    reason: string;
    validUntil?: string;
  };
  monthlyTotal: number;
  pricingBreakdown: {
    basePrice: number;
    premiumFeatures: Array<{
      name: string;
      price: number;
    }>;
    discounts: Array<{
      percentage: number;
      amount: number;
      reason: string;
    }>;
    subtotal: number;
    total: number;
  };
}

const SitePricingManagement: React.FC = () => {
  const [sites, setSites] = useState<SiteWithPricing[]>([]);
  const [filteredSites, setFilteredSites] = useState<SiteWithPricing[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'standard' | 'premium'>('all');
  const [editingSite, setEditingSite] = useState<SiteWithPricing | null>(null);
  const [showAddDiscount, setShowAddDiscount] = useState<string | null>(null);
  const [showFeatureEdit, setShowFeatureEdit] = useState<string | null>(null);
  const [initializing, setInitializing] = useState(false);

  const filterSites = useCallback(() => {
    let filtered = sites;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(site => 
        site.siteId.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        site.siteId.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        site.billingInfo.companyName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(site => {
        const hasPremiumFeatures = Object.values(site.premiumFeaturePricing).some(feature => feature.enabled);
        return statusFilter === 'premium' ? hasPremiumFeatures : !hasPremiumFeatures;
      });
    }

    setFilteredSites(filtered);
  }, [sites, searchTerm, statusFilter]);

  useEffect(() => {
    loadSites();
  }, []);

  useEffect(() => {
    filterSites();
  }, [filterSites]);

  const loadSites = async () => {
    try {
      setLoading(true);
      const response = await sitePricingAPI.getAllSitePricings();
      setSites(response.data as unknown as SiteWithPricing[]);
    } catch (error) {
      console.error('Failed to load site pricings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSavePricing = async (site: SiteWithPricing) => {
    try {
      // Extract the pricing data without the populated siteId
      const { siteId: populatedSiteId, ...pricingData } = site;
      await sitePricingAPI.updateSitePricing(populatedSiteId._id, pricingData);
      setEditingSite(null);
      loadSites();
    } catch (error) {
      console.error('Failed to update pricing:', error);
    }
  };

  const handleApplyDiscount = async (siteId: string, discount: { percentage: number; reason: string; validUntil?: string }) => {
    try {
      await sitePricingAPI.applyDiscount(siteId, discount.percentage, discount.reason, discount.validUntil);
      setShowAddDiscount(null);
      loadSites();
    } catch (error) {
      console.error('Failed to apply discount:', error);
    }
  };

  const handleRemoveDiscount = async (siteId: string) => {
    try {
      await sitePricingAPI.removeDiscount(siteId);
      loadSites();
    } catch (error) {
      console.error('Failed to remove discount:', error);
    }
  };

  const handleUpdateFeature = async (siteId: string, feature: string, enabled: boolean, price: number) => {
    try {
      await sitePricingAPI.updatePremiumFeaturePricing(siteId, feature, enabled, price);
      setShowFeatureEdit(null);
      loadSites();
    } catch (error) {
      console.error('Failed to update feature:', error);
    }
  };

  const handleInitializePricing = async () => {
    try {
      setInitializing(true);
      const response = await fetch('/api/site-pricing/pricing/initialize', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });
      
      const result = await response.json();
      
      if (response.ok) {
        alert(`Pricing initialization complete!\n\nTotal sites: ${result.data.totalSites}\nCreated pricing: ${result.data.createdPricing}\nSkipped: ${result.data.skippedPricing}`);
        loadSites();
      } else {
        alert(`Error: ${result.error}`);
      }
    } catch (error) {
      console.error('Failed to initialize pricing:', error);
      alert('Failed to initialize pricing. Please try again.');
    } finally {
      setInitializing(false);
    }
  };

  const formatCurrency = (amount: number, currency: string = 'GBP') => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const getStatusBadge = (site: SiteWithPricing) => {
    const hasPremiumFeatures = Object.values(site.premiumFeaturePricing).some(feature => feature.enabled);
    const hasDiscount = site.discounts.percentage > 0;
    
    if (hasPremiumFeatures && hasDiscount) {
      return <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-700">Premium + Discount</span>;
    } else if (hasPremiumFeatures) {
      return <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700">Premium</span>;
    } else if (hasDiscount) {
      return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">Discounted</span>;
    } else {
      return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">Standard</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Site Pricing Management</h1>
          <p className="text-gray-600">Manage subscription pricing for all sites</p>
        </div>
        <div className="flex items-center space-x-4">
          {sites.length === 0 && (
            <button
              onClick={handleInitializePricing}
              disabled={initializing}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${initializing ? 'animate-spin' : ''}`} />
              <span>{initializing ? 'Initializing...' : 'Initialize Pricing'}</span>
            </button>
          )}
          <div className="text-sm text-gray-600">
            <span className="font-medium">{sites.length}</span> total sites
          </div>
          <div className="text-sm text-gray-600">
            <span className="font-medium">{sites.filter(s => Object.values(s.premiumFeaturePricing).some(f => f.enabled)).length}</span> premium
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by site name, code, or company..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Sites</option>
              <option value="standard">Standard Only</option>
              <option value="premium">Premium Features</option>
            </select>
          </div>
        </div>
      </div>

      {/* Sites List */}
      {sites.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
          <div className="text-gray-500 mb-4">
            <DollarSign className="w-12 h-12 mx-auto text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Pricing Data Found</h3>
          <p className="text-gray-600 mb-4">
            {initializing ? 'Initializing pricing data...' : 'Click the "Initialize Pricing" button to create pricing records for all sites.'}
          </p>
          {!initializing && (
            <button
              onClick={handleInitializePricing}
              disabled={initializing}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2 mx-auto disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${initializing ? 'animate-spin' : ''}`} />
              <span>{initializing ? 'Initializing...' : 'Initialize Pricing'}</span>
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Site
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Base Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Premium Features
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Monthly Total
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredSites.map((site) => (
                <tr key={site._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{site.siteId.name}</div>
                      <div className="text-sm text-gray-500">{site.siteId.code}</div>
                      <div className="text-xs text-gray-400">{site.billingInfo.companyName}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(site)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(site.basePrice.amount, site.basePrice.currency)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {Object.entries(site.premiumFeaturePricing)
                        .filter(([_, feature]) => feature.enabled)
                        .map(([name, feature]) => (
                          <div key={name} className="flex items-center space-x-2">
                            <span className="text-gray-600 capitalize">{name.replace(/([A-Z])/g, ' $1').trim()}:</span>
                            <span className="font-medium">{formatCurrency(feature.price, site.basePrice.currency)}</span>
                          </div>
                        ))}
                      {Object.values(site.premiumFeaturePricing).every(f => !f.enabled) && (
                        <span className="text-gray-400">None</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {formatCurrency(site.monthlyTotal, site.basePrice.currency)}
                    </div>
                    {site.discounts.percentage > 0 && (
                      <div className="text-xs text-green-600">
                        {site.discounts.percentage}% discount applied
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setEditingSite(site)}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setShowAddDiscount(site.siteId._id)}
                        className="text-green-600 hover:text-green-900"
                      >
                        <DollarSign className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setShowFeatureEdit(site.siteId._id)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                      {site.discounts.percentage > 0 && (
                        <button
                          onClick={() => handleRemoveDiscount(site.siteId._id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Edit Pricing Modal */}
      {editingSite && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Edit Pricing - {editingSite.siteId.name}</h2>
                <button
                  onClick={() => setEditingSite(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Base Price */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tenant Software Subscription Fee</label>
                  <p className="text-xs text-gray-500 mb-3">This creates a tenant-specific Stripe price and updates only this tenant's subscription.</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <input
                        type="number"
                        step="0.01"
                        value={editingSite.basePrice.amount}
                        onChange={(e) => setEditingSite({
                          ...editingSite,
                          basePrice: { ...editingSite.basePrice, amount: parseFloat(e.target.value) }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <div>
                      <select
                        value={editingSite.basePrice.currency}
                        onChange={(e) => setEditingSite({
                          ...editingSite,
                          basePrice: { ...editingSite.basePrice, currency: e.target.value }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="GBP">GBP</option>
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Billing Info */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Billing Information</label>
                  <div className="space-y-4">
                    <input
                      type="text"
                      placeholder="Company Name"
                      value={editingSite.billingInfo.companyName}
                      onChange={(e) => setEditingSite({
                        ...editingSite,
                        billingInfo: { ...editingSite.billingInfo, companyName: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    <input
                      type="email"
                      placeholder="Contact Email"
                      value={editingSite.billingInfo.contactEmail}
                      onChange={(e) => setEditingSite({
                        ...editingSite,
                        billingInfo: { ...editingSite.billingInfo, contactEmail: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <input
                        type="text"
                        placeholder="Street"
                        value={editingSite.billingInfo.billingAddress.street}
                        onChange={(e) => setEditingSite({
                          ...editingSite,
                          billingInfo: {
                            ...editingSite.billingInfo,
                            billingAddress: { ...editingSite.billingInfo.billingAddress, street: e.target.value }
                          }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                      <input
                        type="text"
                        placeholder="City"
                        value={editingSite.billingInfo.billingAddress.city}
                        onChange={(e) => setEditingSite({
                          ...editingSite,
                          billingInfo: {
                            ...editingSite.billingInfo,
                            billingAddress: { ...editingSite.billingInfo.billingAddress, city: e.target.value }
                          }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end space-x-4">
                  <button
                    onClick={() => setEditingSite(null)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleSavePricing(editingSite)}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center space-x-2"
                  >
                    <Save className="w-4 h-4" />
                    <span>Save Changes</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Discount Modal */}
      {showAddDiscount && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Apply Discount</h2>
                <button
                  onClick={() => setShowAddDiscount(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Discount Percentage</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    placeholder="10"
                    id={`discount-percentage-${showAddDiscount}`}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Reason</label>
                  <input
                    type="text"
                    placeholder="e.g., Promotional discount"
                    id={`discount-reason-${showAddDiscount}`}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Valid Until (optional)</label>
                  <input
                    type="date"
                    id={`discount-validuntil-${showAddDiscount}`}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div className="flex justify-end space-x-4">
                  <button
                    onClick={() => setShowAddDiscount(null)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      const percentage = parseFloat((document.getElementById(`discount-percentage-${showAddDiscount}`) as HTMLInputElement).value);
                      const reason = (document.getElementById(`discount-reason-${showAddDiscount}`) as HTMLInputElement).value;
                      const validUntil = (document.getElementById(`discount-validuntil-${showAddDiscount}`) as HTMLInputElement).value;
                      handleApplyDiscount(showAddDiscount, { percentage, reason, validUntil });
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Apply Discount
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Feature Edit Modal */}
      {showFeatureEdit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Edit Premium Features</h2>
                <button
                  onClick={() => setShowFeatureEdit(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                {Object.entries(sites.find(s => s.siteId._id === showFeatureEdit)?.premiumFeaturePricing || {}).map(([feature, config]) => (
                  <div key={feature} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={config.enabled}
                        onChange={(e) => {
                          handleUpdateFeature(showFeatureEdit, feature, e.target.checked, config.price);
                        }}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <div>
                        <div className="font-medium text-gray-900 capitalize">
                          {feature.replace(/([A-Z])/g, ' $1').trim()}
                        </div>
                        <div className="text-sm text-gray-500">Premium Feature</div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600">£</span>
                      <input
                        type="number"
                        step="0.01"
                        value={config.price}
                        onChange={(e) => {
                          handleUpdateFeature(showFeatureEdit, feature, config.enabled, parseFloat(e.target.value));
                        }}
                        className="w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                      <span className="text-sm text-gray-600">/month</span>
                    </div>
                  </div>
                ))}

                <div className="flex justify-end">
                  <button
                    onClick={() => setShowFeatureEdit(null)}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SitePricingManagement;
